/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */

import clsx from 'clsx';
import React, { type KeyboardEvent, useRef, useState } from 'react';
import { Link as WouterLink } from 'wouter';

import type { BaseRowType, BaseTableProps } from './helpers';
import type { LoadMoreRowProps } from './Row';
import { Header } from './Header';
import { EmptyTableRow, LoadMoreRow, Row } from './Row';

export type KeyboardShortcut<T> = {
  handleKeyPress: (elem: T, e: KeyboardEvent) => void;
};
export type BaseNavTableProps<T extends BaseRowType> = BaseTableProps<T> & {
  getPath: (elem: T) => string;
  onRowHover?: (elem: T) => void;
  keyboardShortcuts?: Record<KeyboardEvent['key'], KeyboardShortcut<T>>;
  hasNextPage?: false;
  stickyHeader?: boolean;
  disableArrowKeyNavigation?: boolean;
  /** Wraps each rendered row, e.g. in a drag-and-drop sortable container. */
  renderRowWrapper?: (elem: T, row: React.ReactNode) => React.ReactNode;
};
export type NavTableProps<T extends BaseRowType> =
  | BaseNavTableProps<T>
  | (Omit<BaseNavTableProps<T>, 'hasNextPage'> & {
      hasNextPage: true;
    } & LoadMoreRowProps);
/**
 * A Nav Table
 * @param data - an array of data
 * @param selected - whether or not the row has been selected
 * @param getPath: - retrieves the href for a rendered row
 * @param schema - the table schema. See `ColumnDefinition`
 * @param emptyValue - a mapping of row title to string. Rendered when no data is present
 */
export const NavTable = <T extends BaseRowType>(props: NavTableProps<T>) => {
  const {
    data,
    selected,
    onRowHover,
    getPath,
    schema,
    keyboardShortcuts,
    emptyValue,
    hasNextPage,
    'data-testid': dataTestId,
    textSize = 'sm',
    dottedHeader,
    borderedRows,
    stickyHeader = true,
    rowHasNoAffect,
    backgroundColor,
    disableArrowKeyNavigation = false,
    renderRowWrapper,
  } = props;
  // In some cases, we will arrive at this table with a member already selected
  // due to deeplinks. If that's the case, pre-set the focus index. Otherwise, set to 0
  const selectedIdx = data.findIndex((row) => row.id === selected?.id);
  const [focusedIdx, setFocusedIdx] = useState<number>(
    selectedIdx < 0 ? 0 : selectedIdx
  );
  const navTableRef = useRef<HTMLDivElement>(null);
  return (
    <div
      className={clsx('min-h-f0', textSize === 'sm' ? '-mx-f2' : '-mx-f0')}
      role="table"
      aria-label="Table"
      data-testid={dataTestId}
      ref={navTableRef}
    >
      <div
        className={stickyHeader ? 'sticky -top-f4 pt-f4 -mt-f4 bg-canvas' : ''}
      >
        <Header
          textSize={textSize}
          schema={schema}
          dottedHeader={dottedHeader}
        />
      </div>
      {/* the table height should be the left overspace after subtracting padding and other */}
      {/* component heights in the main pane */}
      {data.length > 0 && (
        <div className="overflow-auto h-[calc(100%_-_27px)] z-0 pb-f2">
          {data.map((row, idx) => {
            // href is passed to anchor via wouter link
            const rowNode = (
              <WouterLink href={getPath(row)}>
                <a
                  data-testid="nav-table-row"
                  className="group outline-none"
                  data-idx={String(idx)}
                  onMouseOver={onRowHover ? () => onRowHover(row) : undefined}
                  onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                    if (keyboardShortcuts && e.key in keyboardShortcuts) {
                      keyboardShortcuts[e.key].handleKeyPress(row, e);
                    }
                    if (disableArrowKeyNavigation) {
                      return;
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      const index = idx === 0 ? 0 : idx - 1;
                      navTableRef.current
                        ?.querySelector<HTMLAnchorElement>(
                          `a[data-idx="${index}"]`
                        )
                        ?.focus();
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const index = idx === data.length ? data.length : idx + 1;
                      navTableRef.current
                        ?.querySelector<HTMLAnchorElement>(
                          `a[data-idx="${index}"]`
                        )
                        ?.focus();
                    }
                  }}
                  onFocus={() => setFocusedIdx(idx)}
                  tabIndex={idx === focusedIdx ? 0 : -1}
                >
                  <Row
                    textSize={textSize}
                    index={idx}
                    value={row}
                    selected={selected?.id === row.id}
                    schema={schema}
                    bordered={borderedRows}
                    isLast={idx === data.length - 1}
                    rowHasNoAffect={
                      rowHasNoAffect ? rowHasNoAffect(row) : undefined
                    }
                    backgroundColor={backgroundColor}
                  />
                </a>
              </WouterLink>
            );
            return (
              <React.Fragment key={String(row.id)}>
                {renderRowWrapper ? renderRowWrapper(row, rowNode) : rowNode}
              </React.Fragment>
            );
          })}
          {hasNextPage && (
            // can't destructure because props are only present when hasNextPage is true
            <LoadMoreRow
              // eslint-disable-next-line react/destructuring-assignment
              isLoading={props.isLoading}
              // eslint-disable-next-line react/destructuring-assignment
              loadMore={props.loadMore}
              textSize={textSize}
              bordered={borderedRows}
            />
          )}
        </div>
      )}
      {/* If no rows, optionally display a sentinel row to avoid a weird UX */}
      {/* Use data.length, not !data for array-ish objects (used in stories) */}
      {data.length === 0 && emptyValue && (
        <EmptyTableRow
          textSize={textSize}
          value={emptyValue}
          schema={schema}
          bordered={borderedRows}
        />
      )}
    </div>
  );
};
